import { LinkOutlined } from '@ant-design/icons';
import { Button, Input, Switch, Tooltip, Typography } from 'antd';
import { FieldData } from 'rc-field-form/es/interface';
import { Trans, useTranslation } from 'react-i18next';

import Card from '../../../Card';
import useAPIQuery from '../../../api/useAPIQuery';
import Config from '../../../app/config';
import Form from '../../../components/Form';
import useTypedParams from '../../../hooks/useTypedParams';
import { JobPostingId } from '../../../types';
import { handleJobPostingDescriptionLengthValidator } from '../../../validators/jobPostingDescriptionValidator';

interface CareersPageFormProps {
  loading?: boolean;
  disabled?: boolean;
}

const CareersPageForm = ({ loading, disabled }: CareersPageFormProps) => {
  const { t } = useTranslation();
  const { tenant, id } = useTypedParams({ tenant: String, id: JobPostingId });

  const jobPosting = useAPIQuery(`/job-postings/${id}/`);

  const vacancyPortalLink = jobPosting.data?.external_publication
    ? `${Config.REACT_APP_CAREER_PAGE_URL}${tenant}/${id}`
    : `${Config.REACT_APP_CAREER_PAGE_URL}${tenant}/`;

  const unlistedExternalPublicationValue = Form.useWatch<boolean>('unlistedExternalPublication');

  return (
    <Form.Item
      shouldUpdate={(prev, next) =>
        prev.externalPublication !== next.externalPublication ||
        prev.unlistedExternalPublication !== next.unlistedExternalPublication
      }
    >
      {(form) => (
        <Card
          title={
            <Typography.Text strong>
              <Trans>Página de Carreiras</Trans>
            </Typography.Text>
          }
        >
          <Form.Row align="middle">
            <Tooltip
              title={
                unlistedExternalPublicationValue
                  ? t(
                      'A vaga não pode ser divulgada na página de carreiras e como não listada ao mesmo tempo'
                    )
                  : null
              }
            >
              <Form.Col>
                <Form.Item name="externalPublication" valuePropName="checked" noStyle>
                  <Switch
                    data-testid="external-publication-switch"
                    defaultChecked={jobPosting.data?.external_publication}
                    loading={loading}
                    disabled={disabled}
                    onClick={async (checked) => {
                      form.setFieldsValue({
                        externalPublication: checked,
                        ...(checked
                          ? {
                              unlistedExternalPublication: false,
                            }
                          : {
                              linkedinExternalPublication: false,
                              careerjetExternalPublication: false,
                              joobleExternalPublication: false,
                              netvagasExternalPublication: false,
                              googleForJobsExternalPublication: false,
                            }),
                      });
                      try {
                        if (checked) {
                          await form.validateFields([
                            'externalPublicationPeriod',
                            'description',
                            'country',
                          ]);
                          await handleJobPostingDescriptionLengthValidator(
                            undefined,
                            jobPosting.data?.description
                          );
                        }
                        form.submit();
                      } catch (err) {
                        form.setFieldsValue({
                          externalPublication: false,
                        });
                        if (err && typeof err === 'object' && 'errorFields' in err) {
                          form.setFields(err.errorFields as FieldData[]);
                        }
                      }
                    }}
                  />
                </Form.Item>
              </Form.Col>
            </Tooltip>
            <Form.Col flex={1}>
              <Form.Item noStyle shouldUpdate>
                <Input
                  data-testid="portal-link-input"
                  value={vacancyPortalLink}
                  readOnly
                  disabled
                  suffix={<LinkOutlined />}
                />
              </Form.Item>
            </Form.Col>
            <Form.Col>
              <Tooltip
                trigger="click"
                getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                title={
                  <>
                    <Typography.Text style={{ color: 'white' }}>
                      <Trans>Link copiado!</Trans>
                    </Typography.Text>
                  </>
                }
              >
                <Button
                  onClick={async () => {
                    await navigator.clipboard.writeText(vacancyPortalLink);
                  }}
                >
                  <Trans>Copiar link</Trans>
                </Button>
              </Tooltip>
            </Form.Col>
          </Form.Row>
        </Card>
      )}
    </Form.Item>
  );
};

export default CareersPageForm;
